import React, {useRef, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faShare} from "@fortawesome/free-solid-svg-icons";
import {Button} from "@progress/kendo-react-buttons";
import {Slide} from "@progress/kendo-react-animation";
import {Notification, NotificationGroup} from "@progress/kendo-react-notification";
import {GearsScoreboard} from "./GearsScoreboard";
import {useClipboardPermission} from "../hooks/useClipboardPermission";
import {canShareResults, shareResults} from "../utils/share";

interface ResultsViewProps {
    scoreBoard: Array<boolean | undefined>;
    total: number;
    lapsToday: number;
    daysInARow: number;
    /** False on the /result route when the player hasn't played today. */
    playedToday: boolean;
    /** When provided, Replay invokes this; otherwise it links back to "/". */
    onReplay?: () => void;
}

function resultMessage(score: number): string {
    const messages = new Map<number, string>([
        [0, "Do you actually own a vehicle?"],
        [1, "Maybe you should switch to a bike?"],
        [2, "Not bad... for a Tesla owner."],
        [3, "50%... respectable."],
        [4, "A perfect score is in your reach. Keep trying... we believe in you!"],
        [5, "TOTALLY worth bragging to your friends about. Nice!"],
        [6, "You 100% have grease in your veins. Well done!"],
    ]);
    return messages.get(score) ?? "Did you check your volume?";
}

/** End-of-lap results: gears image, score, streaks, and sharing. */
export const ResultsView = ({scoreBoard, total, lapsToday, daysInARow, playedToday, onReplay}: ResultsViewProps) => {
    const [gameUrl] = useState(document.location.href);
    const [showShareCancelled, setShowShareCancelled] = useState(false);
    const [showClipboardCopied, setShowClipboardCopied] = useState(false);
    const canUseClipboard = useClipboardPermission();

    const gearSvg = useRef<SVGSVGElement | null>(null);
    const clipboardSvg = useRef<HTMLImageElement | null>(null);
    const extraCanvasRef = useRef<HTMLCanvasElement | null>(null);

    const score = scoreBoard.filter((v) => v === true).length;

    const share = async () => {
        if (!gearSvg.current || !extraCanvasRef.current) {
            return;
        }
        const outcome = await shareResults({
            svg: gearSvg.current,
            canvas: extraCanvasRef.current,
            scoreBoard: scoreBoard.map(Boolean),
            score,
            total,
            lapsToday,
            gameUrl,
            hiddenResultsEl: document.querySelector("#hidden-results"),
            previewImg: clipboardSvg.current,
            canUseClipboard,
        });
        if (outcome === "cancelled") {
            setShowShareCancelled(true);
            setTimeout(() => setShowShareCancelled(false), 2000);
        } else if (outcome === "copied") {
            setShowClipboardCopied(true);
            setTimeout(() => setShowClipboardCopied(false), 2000);
        }
    };

    return (
        <>
            <div className={"k-flex-30"} style={{marginTop: "2rem"}}>
                <GearsScoreboard ref={gearSvg} scoreBoard={playedToday ? scoreBoard : []}
                                 className="gears-result" id="result-gear-image"/>
                {playedToday ? <>
                    <h2>You scored {score} out of {total}</h2>
                    <p className={"fun-message"}>{resultMessage(score)}</p>
                </> : <h2>You have not played today</h2>}
                <p className={"streaks"}>
                    {lapsToday} {lapsToday === 1 ? "lap" : "laps"} today<br/>{daysInARow} day streak!!
                </p>
                {canShareResults(canUseClipboard) &&
                    <>
                        <Button themeColor={"inverse"} className={"share-button"} onClick={share}>
                            SHARE <FontAwesomeIcon className={"share-icon"} icon={faShare}/>
                        </Button>
                        <br/>
                        <a href={"/"} className={"more-info-btn"}
                           onClick={onReplay && ((e) => {
                               e.preventDefault();
                               onReplay();
                           })}>Replay</a>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <a href={"/info"} className={"more-info-btn"}>More info</a>
                    </>
                }
            </div>
            <div id={"hidden-results"}>
                <h3>gEAR'D!! {score}/{total}</h3>
                <p>Play gEAR'D!! today @<a href={gameUrl}>geared.io</a></p>
                <img height={200} width={200} ref={clipboardSvg} src={`${gameUrl}/images/gears.svg`} alt={"score"}/>
            </div>
            <NotificationGroup
                style={{
                    right: 5,
                    bottom: 5,
                    alignItems: "flex-start",
                    flexWrap: "wrap-reverse",
                }}
            >
                <Slide direction={showShareCancelled ? "up" : "down"}>
                    {showShareCancelled && (
                        <Notification type={{style: "info", icon: true}}>
                            Share cancelled
                        </Notification>
                    )}
                </Slide>
                <Slide direction={showClipboardCopied ? "up" : "down"}>
                    {showClipboardCopied && (
                        <Notification type={{style: "success", icon: true}}>
                            Results are copied to the clipboard
                        </Notification>
                    )}
                </Slide>
            </NotificationGroup>
            <canvas className={"hidden"} ref={extraCanvasRef} height={"320px"} width={"320px"}/>
        </>
    );
};
